import { Button } from '@workspace/ui/components/button'
import { Input } from '@workspace/ui/components/input'
import { Paperclip } from 'lucide-react'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'

export interface ChatInputBarHandle {
  focus: () => void
}

interface ChatInputBarProps {
  onSend: (message: string) => void
  onFileSelect?: (file: File) => void
  disabled?: boolean
}

export const ChatInputBar = forwardRef<ChatInputBarHandle, ChatInputBarProps>(function ChatInputBar(
  { onSend, onFileSelect, disabled },
  ref
) {
  const [value, setValue] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }))

  function handleSubmit() {
    const trimmed = value.trim()
    if (trimmed && !disabled) {
      onSend(trimmed)
      setValue('')
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      onFileSelect?.(file)
      // Reset so the same file can be re-selected
      e.target.value = ''
    }
  }

  return (
    <div className="flex gap-3">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        variant="ghost"
        size="icon"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        className="shrink-0 text-muted-foreground hover:text-white"
      >
        <Paperclip className="size-5" />
      </Button>
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        placeholder="Type a message..."
        autoFocus
        className="flex-1"
      />
      <Button onClick={handleSubmit} disabled={!value.trim() || disabled}>
        Send
      </Button>
    </div>
  )
})
